import { EmptyArray, groupBy, last, max, min, sortBy, sum } from 'lib/array/arrayUtils'
import {
  isAccommodationItem,
  isBNBLLEHotelItem,
  isBedbankItem,
  isCruiseItem,
  isInstantBookingLEHotelItem,
  isLEHotelItem,
  isTourV1Item,
  isTourV2Item,
  isInstantLEHotelPostPurchaseItem,
  isBNBLHotelSetDatesItem,
  findPostPurchaseCheckout,
  isVillaItem,
} from 'lib/checkout/checkoutUtils'
import { createSelector } from 'reselect'
import { excludeNullOrUndefined } from 'checkout/utils'
import { getFlightTotals } from 'checkout/selectors/payment/flights'
import { getHasLuxPlusOrSubscriptionInCart } from 'checkout/selectors/view/luxPlusSubscription'
import {
  LE_CONTRACTED_HOTEL_TYPES,
  OFFER_TYPE_ALWAYS_ON,
  OFFER_TYPE_BED_BANK,
  OFFER_TYPE_CRUISE,
  OFFER_TYPE_HOTEL,
  OFFER_TYPE_TOUR,
  OFFER_TYPE_TOUR_V2,
} from 'constants/offer'
import { getCruiseBreakdownView } from 'checkout/lib/utils/cruises/view'
import { getCurrentIntl } from 'i18n/util/currentIntl'
import { getCheckoutCruiseOfferView } from './cruise'
import { getTourItems, getTourV2BreakdownView, getTourV2ExperienceItems, getTourV2Items } from './toursv2'
import { getHotelBreakdownView } from './hotels'

import { getFlightItems, getFlightLuxPlusRegularPrice } from './flights'
import {
  CHECKOUT_ITEM_TYPE_TOUR_V2,
} from 'constants/checkout'
import { CheckoutPageId } from 'checkout/constants/pages'
import { countTravellersAsOccupants } from 'lib/offer/occupancyUtils'
import getTransferItemsView from 'checkout/selectors/view/itemViews/getTransferItemsView'
import { getVillaItems } from './villa'
import { getCarHireItems } from './carHire'
import { dateFormattingLocaleFromGeo, diffInDays } from 'lib/datetime/dateUtils'
import { getDefaultAirport } from 'selectors/flightsSelectors'
import { isLEOffer } from 'lib/offer/offerTypes'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import moment from 'moment'
import getBedbankItemViews from './itemViews/getBedbankItemViews'
import getTourV2ItemViews from './itemViews/getTourV2ItemViews'
import getHotelItemViews from './itemViews/getHotelItemViews'
import getTourV1ItemViews from './itemViews/getTourV1ItemViews'
import getExperienceItemsView from './itemViews/getExperienceItemsView'
import getBedbankItems from './items/getBedbankItems'
import getExperienceItems from './items/getExperienceItems'
import { reselectShallowEqual } from 'lib/selector/selectorUtils'
import { FLIGHTS_MAX_LEAD_TIME_DAYS } from 'constants/flight'
import { buildBundledExperienceTotalsById } from 'checkout/lib/utils/accommodation/bundledExperienceTotals'

// will exempt this as part of change as it has tour optional experiences
const isPostPurchaseAncillaryPayment = createSelector(
  (state: App.State) => findPostPurchaseCheckout(state.checkout.cart.mode),
  (postPurchase) => postPurchase === 'deposit-balance' ||
    postPurchase === 'instalment-balance' ||
    postPurchase === 'instalment' ||
    postPurchase === 'reserve-balance' ||
    postPurchase === 'tour-optional-experiences' ||
    postPurchase === 'tour-accommodation' ||
    postPurchase === 'payment-schedule-balance',
)

const hidePrices = createSelector(
  (state: App.State) => isPostPurchaseAncillaryPayment(state),
  (state: App.State) => state.checkout.cart.mode === 'tour-change-dates',
  (state: App.State) => state.router.location.pathname,
  (isAncillaryPayment, isPostPurchaseTourDateChange, pathname) => isAncillaryPayment || isPostPurchaseTourDateChange || last(pathname.split('/')) === CheckoutPageId.LuxPlus,
)

export function checkoutAccommodationGroupingKey(item: App.Checkout.AccommodationItem): string {
  if (isBNBLLEHotelItem(item)) {
    return `${item.offerId}-bnbl-${item.duration}`
  }

  else if (isTourV2Item(item)) {
    return `${item.offerId}-${item.purchasableOption.fkDepartureId}`
  }

  else if (isCruiseItem(item)) {
    return `${item.offerId}-${item.departureId}`
  }

  else if (item.itemType === 'tourV1') {
    return `${item.offerId}-${item.startDate}-${item.endDate}`
  }

  else {
    return `${item.offerId}-${item.checkIn}-${item.checkOut}`
  }
}

const getCartAccommodationItems = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (items): Array<App.Checkout.AccommodationItem> => items.filter(isAccommodationItem),
  reselectShallowEqual,
)

const getOrderAccommodationItems = createSelector(
  (state: App.State) => state.checkout.cart.existingOrder,
  (state: App.State) => state.checkout.cart.mode,
  (existingOrder, cartMode): Array<App.Checkout.AccommodationItem> => {
    if (existingOrder) {
      return [
        ...existingOrder.items.flatMap<App.Checkout.AccommodationItem>((item) => {
          if ('roomRateId' in item.package && item.reservationType === 'instant_booking' && item.reservation) {
            return [{
              itemId: item.id,
              transactionKey: item.transactionKey,
              offerId: item.offer.id,
              duration: item.duration || 0,
              itemType: 'hotel',
              packageId: item.package.id,
              roomRateId: item.package.roomRateId,
              reservationType: item.reservationType,
              checkIn: item.reservation.startDate,
              checkOut: item.reservation.endDate,
              occupancy: {
                adults: item.reservation.adults,
                teenagers: item.reservation.teenagers,
                children: item.reservation.children,
                infants: item.reservation.infants,
                childrenAge: item.reservation.childrenAge,
              },
            }]
          } else {
            return []
          }
        }),
        ...existingOrder.bedbankItems.flatMap<App.Checkout.BedbankHotelItem>((item) => {
          return item.rooms.map(room => ({
            itemId: item.id,
            sessionId: '',
            roomId: item.roomTypeId,
            checkIn: moment(item.checkIn).format(ISO_DATE_FORMAT),
            checkOut: moment(item.checkIn).format(ISO_DATE_FORMAT),
            roomRateId: item.roomRateId,
            isFlightBundle: room.isFlightBundle,
            bedGroupId: '',
            transactionKey: item.transactionKey,
            offerId: item.offer.id,
            duration: item.duration,
            newPrice: item.total,
            itemType: 'bedbankHotel',
            occupancy: room.occupancy,
          }))
        }),
        ...existingOrder.tourItems.flatMap<App.Checkout.TourV2Item>(item => {
          const isPostPurchaseTourOptionalExperience = cartMode === 'tour-optional-experiences'
          const travellersByRooms = Array.from(groupBy(
            item.tour.travellers,
            (traveller) => traveller.roomNumber,
          ).values())
          return travellersByRooms.map((travellers: Array<App.Tours.TourV2OrderItemTravellerDetails>, index: number) => {
            // as tour item is the total of all travellers, we only want to show the total price on the first item
            const total = index === 0 ? item.total : 0
            return {
              startDate: item.tour.startDate,
              endDate: item.tour.endDate,
              occupancy: countTravellersAsOccupants(travellers),
              duration: item.tour.duration,
              image: item.tour.images?.[0] ?? {},
              itemId: item.id,
              itemType: CHECKOUT_ITEM_TYPE_TOUR_V2,
              offerId: item.tourId,
              transactionKey: item.transactionKey,
              purchasableOption: {
                fkDepartureId: item.departureId,
                fkSeasonId: item.tour.seasonId,
                fkTourId: item.tourId,
                roomType: travellers[0].roomType as App.Tours.V2OfferRoomType,
                fkVariationId: item.tourOptionId ?? '',
                inventoryLeft: 0, // not stored on the order item
                fullPrice: isPostPurchaseTourOptionalExperience ? 0 : total,
                price: isPostPurchaseTourOptionalExperience ? 0 : total,
                memberPrice: isPostPurchaseTourOptionalExperience ? 0 : item.memberPrice,
                packageType: item.packageOption?.packageOption,
              },
            } as App.Checkout.TourV2Item
          })
        }),
      ]
    }

    return []
  },
)
export const getAccommodationItems = createSelector(
  (state: App.State) => getCartAccommodationItems(state),
  (state: App.State) => getOrderAccommodationItems(state),
  (state: App.State) => isPostPurchaseAncillaryPayment(state),
  (accommodationItemsFromCart, accommodationItemsFromOrder, isAncillaryPayment): Array<App.Checkout.AccommodationItem> => {
    return isAncillaryPayment ? accommodationItemsFromOrder : accommodationItemsFromCart
  },
)

export const getTourV2CheckoutItems = createSelector(
  (state: App.State) => getAccommodationItems(state),
  (items): Array<App.Checkout.TourV2Item> => items.filter(isTourV2Item),
)

export const getLeAccommodationItems = createSelector(
  (state: App.State) => getAccommodationItems(state),
  (items): Array<App.Checkout.LEHotelItem> => items.filter(isLEHotelItem),
  reselectShallowEqual,
)

export const getLeInstantPostPurchaseItems = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (items): Array<App.Checkout.InstantBookingLEHotelPostPurchaseItem> => items.filter(isInstantLEHotelPostPurchaseItem),
  reselectShallowEqual,
)

export const getBNBLHotelPostPurchaseItems = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (items): Array<App.Checkout.BNBLLEHotelSetDatesItem> => items.filter(isBNBLHotelSetDatesItem),
  reselectShallowEqual,
)

export const getLeInstantBookingItems = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (items): Array<App.Checkout.InstantBookingLEHotelItem> => items.filter(isInstantBookingLEHotelItem),
  reselectShallowEqual,
)

export const checkoutAccommodationOfferView = createSelector(
  (state: App.State) => getCheckoutCruiseOfferView(state),
  (state: App.State) => getBedbankItemViews(state),
  (state: App.State) => getTourV2ItemViews(state),
  (state: App.State) => getHotelItemViews(state),
  (state: App.State) => getTourV1ItemViews(state),
  (
    cruiseView,
    bedbankItemViews,
    tourV2ItemViews,
    hotelItemViews,
    tourV1ItemViews,
  ): App.WithDataStatus<Array<App.Checkout.AccommodationOfferView>> => {
    return {
      hasRequiredData: tourV1ItemViews.hasRequiredData &&
        cruiseView.hasRequiredData &&
        bedbankItemViews.hasRequiredData &&
        tourV2ItemViews.hasRequiredData &&
        hotelItemViews.hasRequiredData,
      data: [
        ...hotelItemViews.data,
        ...tourV1ItemViews.data,
        ...bedbankItemViews.data,
        ...cruiseView.data,
        ...tourV2ItemViews.data,
      ].filter(excludeNullOrUndefined),
    }
  },
)

export const getAccommodationBreakdownView = createSelector(
  (state: App.State) => checkoutAccommodationOfferView(state),
  (state: App.State) => getFlightTotals(state),
  (state: App.State) => getFlightLuxPlusRegularPrice(state),
  (state: App.State) => hidePrices(state),
  (state: App.State) => findPostPurchaseCheckout(state.checkout.cart.mode),
  (state: App.State) => getHasLuxPlusOrSubscriptionInCart(state),
  (state: App.State) => state.checkout.commissionData,
  (state: App.State) => getFlightItems(state),
  (state: App.State) => state.flights.journeysById,
  (state: App.State) => getExperienceItems(state),
  (state: App.State) => getExperienceItemsView(state),
  (state: App.State) => getTransferItemsView(state),
  (state: App.State) => state.geo,
  (
    offerViewsWithStatus,
    flightsTotals,
    flightLuxPlusRegularPrice,
    hidePrice,
    postPurchase,
    hasLuxPlusOrSubscriptionInCart,
    commissionData,
    flightItems,
    journeysById,
    experienceItems,
    experienceItemsView,
    transferItemsView,
    geo,
  ): App.WithDataStatus<{ tours: Array<App.Checkout.PriceBreakdownView>, accommodations: Array<App.Checkout.PriceBreakdownView> }> => {
    if (!offerViewsWithStatus.hasRequiredData) { return { hasRequiredData: false, data: { accommodations: EmptyArray, tours: EmptyArray } } }

    const dateFormattingLocale = dateFormattingLocaleFromGeo(geo)

    const bundledExperienceTotalsById = buildBundledExperienceTotalsById(experienceItemsView.data, transferItemsView.data)
    for (const item of experienceItems) {
      if (!item.isAccomBundle || bundledExperienceTotalsById[item.isAccomBundle]) continue
      bundledExperienceTotalsById[item.isAccomBundle] = { price: 0, memberPrice: 0 }
    }

    const { accommodations, tours } = offerViewsWithStatus.data.reduce<{ accommodations: Array<App.Checkout.PriceBreakdownView>, tours: Array<App.Checkout.PriceBreakdownView> }>((acc, offerView) => {
      switch (offerView.offerType) {
        case OFFER_TYPE_CRUISE:
          acc.accommodations.push(getCruiseBreakdownView(getCurrentIntl(), offerView, hidePrice))
          break
        case OFFER_TYPE_TOUR_V2:
          acc.tours.push(getTourV2BreakdownView(offerView, hidePrice, postPurchase, commissionData, dateFormattingLocale))
          break
        case OFFER_TYPE_ALWAYS_ON:
        case OFFER_TYPE_TOUR: // Not enabled yet
        case OFFER_TYPE_BED_BANK:
        case OFFER_TYPE_HOTEL:
          acc.accommodations.push(getHotelBreakdownView(
            offerView,
            flightsTotals,
            hidePrice,
            hasLuxPlusOrSubscriptionInCart,
            commissionData,
            {
              flightItems,
              journeysById,
              flightLuxPlusRegularPrice: flightLuxPlusRegularPrice.data,
              bundledExperienceTotalsById,
              dateFormattingLocale,
            },
          ))
          break
      }
      return acc
    }, { accommodations: [], tours: [] })

    return {
      hasRequiredData: true,
      data: {
        accommodations: sortBy(accommodations, view => view.startDate, 'asc'),
        tours,
      },
    }
  },
)

export const getHotelTotalPrice = createSelector(
  (state: App.State) => getAccommodationBreakdownView(state),
  (state: App.State) => getFlightTotals(state),
  ({ data: { accommodations } }, flightsTotal) => {
    return sum(accommodations.filter(view => view.productType && LE_CONTRACTED_HOTEL_TYPES.includes(view.productType)), view => view.price) - flightsTotal.data.price
  })

export const getAccommodationOccupantsFromCart = createSelector(
  (state: App.State) => getAccommodationItems(state),
  (items): App.Occupants | undefined => {
    if (items.length) {
      return items.reduce((acc, item) => {
        if ('occupancy' in item && item.occupancy) {
          return {
            adults: acc.adults + item.occupancy.adults,
            children: acc.children + (item.occupancy.children || 0),
            infants: acc.infants + (item.occupancy.infants || 0),
            teenagers: acc.teenagers + (item.occupancy.teenagers || 0),
          }
        }
        return acc
      }, { adults: 0, children: 0, infants: 0, teenagers: 0 })
    }
  },
)

export const getAccommodationStartDate = createSelector(
  (state: App.State) => getAccommodationItems(state),
  (items): string | undefined => {
    const startDates = items
      .map(item => {
        if (isInstantBookingLEHotelItem(item) || isBedbankItem(item) || isVillaItem(item)) {
          return item.checkIn
        }
        if (isTourV1Item(item) || isTourV2Item(item)) {
          return item.startDate
        }
      }).filter(excludeNullOrUndefined)
    return min(startDates, (date) => new Date(date))
  },
)

export const getAccommodationEndDate = createSelector(
  (state: App.State) => getAccommodationItems(state),
  (items): string | undefined => {
    const startDates = items
      .map(item => {
        if (isInstantBookingLEHotelItem(item) || isBedbankItem(item) || isVillaItem(item)) {
          return item.checkOut
        }
        if (isTourV1Item(item) || isTourV2Item(item)) {
          return item.endDate
        }
      }).filter(excludeNullOrUndefined)

    return max(startDates, (date) => new Date(date))
  },
)

export const isCheckoutAccommodationItemsOnly = createSelector(
  (state: App.State) => getLeInstantBookingItems(state),
  (state: App.State) => getBedbankItems(state),
  (state: App.State) => getExperienceItems(state),
  (state: App.State) => getFlightItems(state),
  (state: App.State) => getTransferItemsView(state),
  (state: App.State) => getVillaItems(state),
  (state: App.State) => getCarHireItems(state),
  (state: App.State) => getTourV2Items(state),
  (state: App.State) => getTourItems(state),
  (state: App.State) => getTourV2ExperienceItems(state),
  (leInstantHotelItems, bedbankItems, experienceItems, flightItems, transferItems, villaItems, carHireItems, tourV2Items, tourItems, tourV2ExperienceItems): boolean => {
    return !!leInstantHotelItems.length &&
      !bedbankItems.length &&
      !experienceItems.length &&
      !flightItems.length &&
      !transferItems.data.length &&
      !villaItems.length &&
      !carHireItems.length &&
      !tourV2Items.length &&
      !tourItems.length &&
      !tourV2ExperienceItems.length &&
      !flightItems.length
  },
)

export const canShowAddFlightsForHotelItems = createSelector(
  (state: App.State) => checkoutAccommodationOfferView(state),
  (state: App.State) => getDefaultAirport(state),
  (accommodationViews, defaultUserAirport): boolean => {
    if (!accommodationViews.data.length) return false

    return accommodationViews.data.every(view => view.offerType === OFFER_TYPE_HOTEL) &&
      accommodationViews.data.every(view => view.itemViews.every(itemView => isInstantBookingLEHotelItem(itemView.item))) &&
      accommodationViews.data.every(view => view.startDate && diffInDays(new Date(), new Date(view.startDate)) <= FLIGHTS_MAX_LEAD_TIME_DAYS) &&
      accommodationViews.data.every(view => isLEOffer(view.offer) && view.offer.offerFlightsEnabled && view.offer.flightDestinationPort !== defaultUserAirport?.code)
  },
)
